<template>
  <template v-if="toolboxGroup">
    <IconList size="24" style="padding:10px;"/>
    <p>Choice</p>
  </template>

  <template v-else>

    <div class="set" :style="{
      flexDirection: positionFlex,
      alignItems: tileAlign,
      lineHeight: lineHeight,
      justifyContent: justifyContent,
      width: tileWidth,
      ...elementStyle
    }" :data-element-id="elementId">
      <div class="subset" v-if="showLabel && !labelAfter">
        <ProsemirrorEditor
            :elementId="elementId"
            class="text"
            defaultColor="#333"
            :defaultFontName="theme.fontName"
            :editable="true"
            :viewer="viewer"
            :value="elementText"
            v-if="elementText !== trailingBreak"
            @mouseover="isHovering = true"
            @mouseleave="isHovering = false"
            @update="value => elementData.text = value"
        />
        <Idot :element-data="elementData"/>
      </div>

      <div v-if="elementEditorType === 'select'">
        <SelectButton v-model="numberValue"
                      :options="getComputedChoices"
                      optionLabel="name"
                      optionValue="id"
                      aria-labelledby="basic"
                      @mouseover="isHovering = true"
                      @mouseout="isHovering = false"
                      @focus="isFocused = true"
                      @blur="isFocused = false"
                      :id="elementId + '-choiceSelect'"
        />
      </div>

      <div v-if="isLocale">
        <Dropdown
            v-model="selectedLocale"
            :options="localeOptions"
            optionLabel="name"
            optionValue="code"
            @mouseover="isHovering = true"
            @mouseout="isHovering = false"
            @focus="isFocused = true"
            @blur="isFocused = false"
            :id="elementId + '-choiceLocale'"
        />
      </div>
      <div v-if="isCurrency">
        <Dropdown
            v-model="selectedCurrency"
            :options="currencyOptions"
            :optionLabel="currencyType === 'Code' ? 'code' : 'name'"
            optionValue="code"
            @mouseover="isHovering = true"
            @mouseout="isHovering = false"
            @focus="isFocused = true"
            @blur="isFocused = false"
            :id="elementId + '-choiceCurrency'"
        />
      </div>
      <div v-if="elementEditorType === 'checkbox'">
        <Checkbox
            v-model="getBinaryComputedChoiceId"
            :binary="true"
            optionLabel="name"
            optionValue="id"
            aria-labelledby="basic"
            @mouseover="isHovering = true"
            @mouseout="isHovering = false"
            @focus="isFocused = true"
            @blur="isFocused = false"
            :id="elementId + '-choiceCheckbox'"
        />
      </div>

      <div v-else-if="elementEditorType === 'switch'">
        <InputSwitch
            v-model="getBinaryComputedChoiceId"
            optionLabel="name"
            optionValue="id"
            @mouseover="isHovering = true"
            @mouseout="isHovering = false"
            @focus="isFocused = true"
            @blur="isFocused = false"
            :id="elementId + '-choiceSwitch'"
        />
      </div>
      <div v-else-if="elementEditorType === 'dropdown' && !isLocale && !isCurrency">
        <Dropdown
            ref="dropdownRef"
            v-model="numberValue"
            :options="getComputedChoices"
            optionLabel="name"
            optionValue="id"
            class="choiceDropdown"
            @mouseover="isHovering = true"
            @mouseout="isHovering = false"
            @focus="isFocused = true"
            @blur="isFocused = false"
            :placeholder="placeholder"
            @change="removePlaceholderClass"
            :id="elementId + '-dropdown'"

        />
      </div>
      <div v-else-if="elementEditorType === 'readonly'" :data-element-id="elementId">
        {{ getChoice(givingTreeStore.getWidgetValue(elementHookId).value) }}
      </div>

      <div v-else-if="elementEditorType === 'button'">
        <Button class="choice-button" @click="toggleChoice" :style="{ fontFamily: theme.fontName }">{{ elementChoiceButtonText }}</Button>
      </div>
      <div class="subset" v-if="showLabel && labelAfter">
        <ProsemirrorEditor
            :elementId="elementId"
            class="text"
            defaultColor="#333"
            :defaultFontName="theme.fontName"
            :editable="true"
            :viewer="viewer"
            :value="elementText"
            v-if="elementText !== trailingBreak"
            @mouseover="isHovering = true"
            @mouseleave="isHovering = false"
            @update="value => elementData.text = value"
        />
        <Idot :element-data="elementData"/>
      </div>
    </div>
    <Idot v-if="!showLabel" :element-data="elementData"/>
  </template>

</template>

<script setup lang="ts">
import {computed, getCurrentInstance, nextTick, onMounted, ref, toRefs, watch} from "vue";
import {useGivingTreeStore, useMainStore, useSlidesStore, useWidgetStore} from "@/store";
import useComputedTreeValue from "@/views/components/element/hooks/useComputedTreeValue";
import ProsemirrorEditor from "@/views/components/element/ProsemirrorEditor.vue";
import Idot from "@/components/Idot.vue"
import {storeToRefs} from "pinia";

const toolboxGroup = computed(() => {
  const parentClassList = getCurrentInstance().parent.attrs.class;
  return parentClassList === "dragArea toolbox-group"
});

const props = defineProps({
  elementData: {
    type: Object
  },
  viewer: {
    type: Boolean,
    default: false
  },
  style: Object,
  class: String
})

const givingTreeStore = useGivingTreeStore()
const dropdownRef = ref(null)
const {elementData} = toRefs(props)
const slidesStore = useSlidesStore()

const trailingBreak = '<p style=""><br class="ProseMirror-trailingBreak"></p>'

const isHovering = ref(false)
const isFocused = ref(false)
const elementId = computed(() => elementData.value?.id ?? null)
const elementText = computed(() => elementData.value?.text ?? 'Label')
const elementHookId = computed(() => elementData.value?.hookId ?? '')
const elementStyle = computed(() => elementData.value?.style ?? {})
const elementEditorType = computed(() => elementData.value?.editorType ?? '')
const elementFieldType = computed(() => elementData.value?.fieldType ?? '')

const elementChoiceButtonColor = computed(() => elementData.value?.style?.choiceButtonColor ?? '#2196F3')
const elementChoiceButtonText = computed(() => elementData.value?.choiceButtonText ?? '')

const lineHeight = computed(() => elementData.value?.style?.lineHeight + 'px' ?? '100%')
const labelMargins = computed(() => elementData.value?.labelPosition === 'left' || elementData.value?.labelPosition === 'right' ? '0 10px' : '10px 0')
const positionFlex = computed(() => elementData.value?.labelPosition === 'left' || elementData.value?.labelPosition === 'right' ? 'row' : 'column' )
const showLabel = computed(() => elementData.value?.labelPosition !== 'none')
const labelAfter = computed(() => elementData.value?.labelPosition === 'bottom' || elementData.value?.labelPosition === 'right')


const color = computed(() => elementData.value?.style.color ?? '#6c757d')
const checkboxColor = computed(() => elementData.value?.style.color ?? '#ffffff')
const backgroundColor = computed(() =>
    elementData.value?.editorBackgroundColor
        ? elementData.value.editorBackgroundColor
        : elementData.value?.style.backgroundColor
            ? elementData.value.style.backgroundColor
            : '#2196F3'
);

const isLocale = ref(false)


const elementColor = computed(() => elementStyle.value?.color ?? '')
const fontSize = computed(() => elementStyle.value?.fontSize ?? '')
const fontWeight = computed(() => elementStyle.value?.fontWeight ?? '')
const fontStyle = computed(() => elementStyle.value?.fontStyle ?? '')
const textAlign = computed(() => elementStyle.value?.textAlign ?? '')
const textDecoration = computed(() => elementStyle.value?.textDecoration ?? '')
const dropDownWidth = computed(() => processString(elementStyle.value?.width) ?? 'auto')
const currencyType = computed(() => elementData.value?.currencyType ?? 'Code')
const currencyOptions = computed(() => elementData.value?.currencies ?? [{
  code: 'USD',
  name: new Intl.DisplayNames(['en-US'], { type: 'currency' }).of('USD')
}])

const fontName = computed(() => `"${theme.value.fontName}"` ?? '')

const tileWidth = computed(() => elementStyle.value?.containerWidth ? `${elementStyle.value?.containerWidth}px` : '')
const tileHeight = computed(() => elementStyle.value?.containerHeight ? `${elementStyle.value?.containerHeight}px` : '20px')
const buttonFontSize = computed(() => fontSize.value.length > 0 ? fontSize.value : '16px')

const dropDownArrowVisible = computed(() => isFocused.value || isHovering.value ? 'visible' : 'hidden')
const dropDownArrowBorder = computed(() => isFocused.value || isHovering.value ? '1px solid #DDD' : '1px solid transparent')

const justifyContent = computed(() => elementData.value?.style.justifyContent ?? '')
const tileAlign = computed(() => elementData.value?.style.align ?? 'center')
const inputAlign = computed(() => elementData.value?.style.inputAlign ?? 'center')

const {metaByHookId} = storeToRefs(givingTreeStore)
const {locale, currency, theme} = storeToRefs(slidesStore)


const selectedLocale = ref(locale.value ?? 'en-US')
const selectedCurrency = computed(
    {
      get: () => {
        return givingTreeStore.getWidgetValue('meta-currency').textValue
      },
      set: (val) => {
        givingTreeStore.setWidgetValue('meta-currency', val)
      }
    }
)

const isCurrency = computed(() => props.elementData.hookId === 'meta-currency')



const {
  getActiveHookId,
  setComputedValue,
  getComputedChoices,
  getComputedChoiceId,
  getComputedValue,
  getOverrideComputedChoiceId
} = useComputedTreeValue()

const getChoice = (input) => {
  const variable = givingTreeStore.getWidgetValue(elementHookId.value)
  for (const choice of variable.choices) {
    if (choice.value === input) {
      return choice.name
    }
  }
}

const numberValue = computed({
  get() {
    return elementData.value?.placeholder ? getOverrideComputedChoiceId.value : getComputedChoiceId.value
  },
  set(val) {
    getComputedChoiceId.value = val
  }
})



const removePlaceholderClass = () => {
  if (dropdownRef.value && dropdownRef.value.$el) {
    const dropdownElement = dropdownRef.value.$el

    const placeholderElement = dropdownElement.querySelector('.p-dropdown-label.p-placeholder')

    if (placeholderElement) {
      placeholderElement.classList.remove('p-placeholder')
    }
  }
}

const processString = (widthString) => {
  let newValue = parseInt(widthString)
  return newValue - 45 + 'px'
}
const toggleChoice = () => {
  getBinaryComputedChoiceId.value = !getBinaryComputedChoiceId.value;
};

const getBinaryComputedChoiceId = computed({
  get() {
    return getComputedValue.value !== 0
  },
  set(val) {
    for (const choice of getComputedChoices.value) {
      if ((choice.value !== 0 && val) ||
          (choice.value === 0 && !val)
      ) {
        getComputedValue.value = choice.value
      }
    }
  }
})


const localeOptions = computed(() => {
  const modelLocaleCode = metaByHookId.value['meta-locale'].text
  const browserLocaleCode = navigator.language
  const modelLocaleName = convertToName(modelLocaleCode)
  const browserLocaleName = convertToName(browserLocaleCode)

  if (modelLocaleName === browserLocaleName) {
    return [{code: modelLocaleCode, name: `${modelLocaleName}`}]
  }

  if (modelLocaleCode) {
    return [{code: browserLocaleCode, name: `${browserLocaleName}`}, {code: modelLocaleCode, name: `${modelLocaleName}`}]
  }

  return [{code: browserLocaleCode, name: `${browserLocaleName}`}]

})



const convertToName = (code) => {

  const displayName = new Intl.DisplayNames(code, { type: 'language' });
  return displayName.of(code);

}

watch(selectedLocale, (newVal) => {
  setLocale(newVal)
})

const setLocale = (val) => {
  locale.value = val
}

onMounted(() => {

  if (elementHookId.value) getActiveHookId(elementHookId.value)

  if (elementHookId.value === 'meta-locale' ) {
    isLocale.value = true
  } else if (elementHookId.value === 'meta-currency') {
    isCurrency.value = true
  }

  if (numberValue.value) removePlaceholderClass()

  setLocale(selectedLocale.value)
})

watch(elementHookId, (newVal) => {

  if (newVal) {
    getActiveHookId(newVal)
    const value = givingTreeStore.getWidgetValue(newVal)
    setComputedValue(value.value)
  }

  if (elementHookId.value === 'meta-locale' ) {
    isLocale.value = true
  } else if (elementHookId.value === 'meta-currency') {
    isCurrency.value = true
  }
}, {deep: true})

const placeholder = computed(() => {
  if (elementData.value.placeholder) {
    getActiveHookId(elementHookId.value)
    const choice = getComputedChoices.value.find((choice) => choice.id === getComputedChoiceId.value)

    return choice.name
  }
})

const placeholderColor = computed(() => elementStyle.value?.placeholderColor ?? '')

</script>


<style scoped lang="scss">

:deep(p) {
  margin: v-bind(labelMargins);
}

:deep(.p-dropdown-trigger) {

}

:deep(.p-dropdown-label) {
  font-family: v-bind(fontName);
  text-overflow: clip !important;
}

:deep(.p-dropdown) {
  background-color: transparent;
  border: v-bind(dropDownArrowBorder);
  font-size: v-bind(fontSize);
  color: v-bind(elementColor);
  font-weight: v-bind(fontWeight);
  font-style: v-bind(fontStyle);
  text-align: v-bind(inputAlign);
  text-decoration: v-bind(textDecoration);
  width: v-bind(dropDownWidth);
}

:deep(.choiceDropdown .p-dropdown-label) {
  color: v-bind(elementColor);
}


:deep(.choiceDropdown .p-placeholder) {
  color: v-bind(placeholderColor);
  opacity: 1;
}

:deep(.choiceDropdown .p-placeholder:hover) {
  color: v-bind(placeholderColor);
  opacity: 0.8;
}

:deep(.choiceDropdown .p-placeholder:focus) {
  color: v-bind(placeholderColor);
  opacity: 0.5;
}

.set {
  display: flex;
}

.subset {
  display: flex;
  flex-direction: row;
  align-items: center;
  text-align: left;
}


:deep(.p-dropdown-label) {
  font-size: v-bind(fontSize);
}

:deep(.p-dropdown) {
  background: transparent;
}


:deep(.custom-dropdown) {
  color: v-bind(color);
}

:deep(.custom-dropdown .p-dropdown-label) {
  color: v-bind(color) !important;
}

:deep(.custom-dropdown .p-dropdown-panel) {
  color: v-bind(color);
}

:deep(.custom-dropdown .p-dropdown-item) {
  color: v-bind(color);
}

:deep(.custom-dropdown path) {
  fill: v-bind(color) !important;
}

:deep(.p-checkbox .p-checkbox-box .p-checkbox-icon) {
  color: v-bind(checkboxColor) !important;
}

:deep(.p-checkbox .p-checkbox-box) {
  background: v-bind(backgroundColor) !important;
  border-color: v-bind(checkboxColor);
}

:deep(.p-checkbox.p-highlight .p-checkbox-box) {
  background-color: v-bind(backgroundColor) !important;
  border-color: v-bind(checkboxColor) !important;
}

:deep(.p-inputswitch.p-highlight .p-inputswitch-slider) {
  background: v-bind(backgroundColor) !important;
  border: none;
}

:deep(.p-selectbutton .p-button.p-highlight) {
  background: v-bind(backgroundColor) !important;
}

:deep(.p-button .p-button-label) {
  color: v-bind(color) !important;
  font-family: v-bind(fontName) !important;
}

:deep(.p-button.p-component.choice-button) {
  background: v-bind(elementChoiceButtonColor) !important;
  border: 1px solid v-bind(elementChoiceButtonColor) !important;
  padding: 0 !important;
  justify-content: center;
  text-align: center;
  width: v-bind(tileWidth);
  height: v-bind(tileHeight);
  font-size: v-bind(buttonFontSize);
}

</style>
